import { Box, Button, Card, Flex } from '@chakra-ui/react'
import type { InsuranceParamsVehiclePagedQuery } from 'BrokerTsLib/src/dtos'
import { useSearchParamsStatePersistance } from 'brokertslib/src/hooks/useSearchParamsStatePersistance'
import { createContext, useEffect, useState } from 'react'

import { OffersExportForm } from '@/containers/export/OffersExportForm'
import { useAuth } from '@/context/AuthContext'
import { useApp } from '@/hooks/useApp'
import { Product } from 'BrokerTsLib/src/enums'
import type { IPaginationContext } from 'BrokerTsLib/src/interfaces/Pagination/Pagination'
import { ConceptsList } from '../../containers/concepts/ConceptsList'
import { DashboardHeader } from '../../containers/dashboard/DashboardHeader'
import { PageToolbar } from '../../containers/PageToolbar'
import { ConceptsCarSearch } from '../../containers/search/ConceptsCarSearch'
import { SearchButton } from '../../containers/search/SearchButton'
import { WithAuth } from '../../hoc/WithAuth'
import { useGetAll } from '../../hooks/useInsuranceParams'
import { PageWrapper } from '../../layouts/components/PageWrapper'

export const conceptsCarsContext = createContext<
  IPaginationContext<InsuranceParamsVehiclePagedQuery>
>({} as IPaginationContext<InsuranceParamsVehiclePagedQuery>)

const ConceptsCarPageComponent = () => {
  const { product } = useApp()
  const [filter, setFilter] =
    useSearchParamsStatePersistance<InsuranceParamsVehiclePagedQuery>(
      {
        listType: 1,
        pageIndex: 1,
        pageSize: 10,
      },
      ['listType'],
    )

  const [isOpen, setOpen] = useState(false)
  const [allowSelect, setAllowSelect] = useState(false)

  const [deletedCounter, setDeletedCounter] = useState(0)

  const { data, mutateAsync: getAllAsync, isLoading } = useGetAll()
  const items = data?.data

  const reloadData = () => setDeletedCounter(deletedCounter + 1)

  useEffect(() => {
    if (filter.pageSize)
      // nechci spoustet nacitani na pageload 2x
      getAllAsync(filter)
  }, [filter, deletedCounter])

  const [isExportOpen, setExportOpen] = useState(false)
  const { user } = useAuth()

  return (
    <PageWrapper
      header={<DashboardHeader title="Rozpracované nabídky" />}
      mainProps={{
        sx: { pt: { base: 0, md: 8 }, gap: 4, display: 'grid', alignContent: 'start' },
      }}
    >
      <PageToolbar
        rightElements={
          <Flex sx={{ gap: 4 }}>
            {product != Product.VehicleLeasingInsurance &&
              <Button variant="ghost" onClick={() => setAllowSelect(!allowSelect)}>
                {allowSelect ? 'Zrušit smazání' : 'Vybrat ke smazání'}
              </Button>
            }
            {user?.isSupervisor &&
              <Button onClick={() => setExportOpen(!isExportOpen)}>
                {isExportOpen ? 'Skrýt export' : 'Export'}
              </Button>
            }
            <SearchButton isOpen={isOpen} toggleOpen={() => setOpen(!isOpen)} />
          </Flex>
        }
      />
      <conceptsCarsContext.Provider
        value={{
          loading: isLoading,
          totalCount: items?.totalCount ?? 0,
          currentPage: filter.pageIndex,
          pageSize: filter.pageSize ?? 10,
          filter,
          setCurrentPage: (p) => setFilter(f => ({ ...f, pageIndex: p })),
          setPageSize: (s) => setFilter(f => ({ ...f, pageSize: s })),
          updateFilter: setFilter,
          reload: reloadData,
        }}
      >
        {isOpen && <ConceptsCarSearch />}
        {isExportOpen && <OffersExportForm />}

        <Card>
          <Box
            sx={{
              display: 'grid',
              gap: 8,
              placeItems: 'start',
              justifyItems: 'stretch',
            }}
          >
            <ConceptsList
              concepts={items?.items ?? []}
              withPaginator
              allowSelect={allowSelect}
            />
          </Box>
        </Card>
      </conceptsCarsContext.Provider>
    </PageWrapper>
  )
}

export const ConceptsCarPage = WithAuth(ConceptsCarPageComponent)
